import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./TodayBookings.module.css";
import { Button, CircularProgress, TextField } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { fetchTodayBookings } from "../../api";
import NoData from "../svgIcons/NoData";
import Spinner from "../Spinner/Spinner";

const TodayBookings = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useMemo(() => JSON.parse(localStorage.getItem("profile")), []);
  const cameFromDashboard = Boolean(location.state?.fromDashboard);
  const [bookings, setBookings] = useState([]);
  const [totals, setTotals] = useState({
    totalBookings: 0,
    totalPax: 0,
    totalAmount: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadBookings = useCallback(async (showRefresh = false) => {
    try {
      setError("");
      showRefresh ? setRefreshing(true) : setLoading(true);

      const creator =
        user?.result?.userType === "user" ? user?.result?._id : undefined;
      const { data } = await fetchTodayBookings(creator);

      setBookings(data.bookings || []);
      setTotals({
        totalBookings: data.totalBookings || 0,
        totalPax: data.totalPax || 0,
        totalAmount: data.totalAmount || 0,
      });
    } catch (err) {
      console.error("Today bookings failed", err);
      setError("Unable to load today's bookings. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    loadBookings();
  }, [history, loadBookings, user]);

  const filteredBookings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return bookings;

    return bookings.filter((booking) =>
      [
        booking.invoiceNumber,
        booking.client?.name,
        booking.client?.agentCode,
        booking.hotel,
        booking.refNum,
        booking.nationality,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [bookings, searchQuery]);

  const openInvoice = (id) => {
    history.push({
      pathname: `/invoice/${id}`,
      state: { fromTodayBookings: true },
    });
  };

 const goBack = () => {
  history.push("/dashboard");
};
  const formatAmount = (value, currency = "฿") =>
    `${currency} ${Number(value || 0).toLocaleString("en-IN")}`;

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          {cameFromDashboard && (
            <Button
              className={styles.backButton}
              startIcon={<ArrowBackIcon />}
              onClick={goBack}
            >
              Back
            </Button>
          )}
          <div>
            <span className={styles.eyebrow}>Today</span>
            <h1>Today's Bookings</h1>
            <p>{moment().format("dddd, Do MMM YYYY")}</p>
          </div>
        </div>
        <Button
          className={styles.refreshButton}
          onClick={() => loadBookings(true)}
          disabled={refreshing}
        >
          {refreshing ? <CircularProgress size={18} /> : "Refresh"}
        </Button>
      </header>

      <section className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <span>Bookings</span>
          <strong>{totals.totalBookings}</strong>
        </div>
        <div className={styles.summaryCard}>
          <span>Total Pax</span>
          <strong>{totals.totalPax}</strong>
        </div>
        <div className={styles.summaryCard}>
          <span>Total Amount</span>
          <strong>{formatAmount(totals.totalAmount, bookings[0]?.currency)}</strong>
        </div>
      </section>

      <section className={styles.contentPanel}>
        <div className={styles.toolbar}>
          <div>
            <h2>Booking List</h2>
            <p>{filteredBookings.length} record(s) showing</p>
          </div>
          <TextField
            className={styles.searchInput}
            variant="outlined"
            placeholder="Search bookings..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: <SearchIcon className={styles.searchIcon} />,
            }}
          />
        </div>

        <div className={styles.tableShell}>
          {error ? (
            <div className={styles.emptyState}>{error}</div>
          ) : filteredBookings.length === 0 ? (
            <div className={styles.emptyState}>
              <NoData />
              <p>No bookings for today.</p>
            </div>
          ) : (
            <table className={styles.bookingTable}>
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Agent</th>
                  <th>Hotel</th>
                  <th>Ref No</th>
                  <th>Pax</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking._id} onClick={() => openInvoice(booking._id)}>
                    <td data-label="Booking">
                      <strong>{booking.invoiceNumber || "N/A"}</strong>
                      <span>{moment(booking.date).format("hh:mm A")}</span>
                    </td>
                    <td data-label="Agent">
                      <strong>{booking.client?.name || "Cash"}</strong>
                      <span>{booking.client?.agentCode || "-"}</span>
                    </td>
                    <td data-label="Hotel">{booking.hotel || "-"}</td>
                    <td data-label="Ref No">{booking.refNum || "-"}</td>
                    <td data-label="Pax">
                      {booking.pax || Number(booking.adult || 0) + Number(booking.child || 0)}
                    </td>
                    <td data-label="Amount">
                      {formatAmount(booking.total, booking.currency)}
                    </td>
                 
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
};

export default TodayBookings;
